import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { ParentItemBasicInfo } from 'src/app/models/dto/parent-item-basic-info-dto';
import { ApolloClientService } from './apollo-client.service';
import { GraphQLParentItemBasicInfoOutput } from 'src/app/graphQL/models/output/graphql-base-test-item-output';
import { GET_PARENT_ITEMS_QUERY, TRY_GET_LOCKED_ITEM_UID_AND_TYPE_QUERY } from 'src/app/graphQL/queries/item-queries';
import { convertGraphQLItemUidAndTypeOutputToDto, convertGraphQLParentItemBasicInfoOutputToDto } from 'src/app/graphQL/mappers/common.mappers';
import { TRY_LOCK_ITEM_MUTATION, UNLOCK_ITEM_MUTATION, FDD_LOCK_ITEM_MUTATION, FDD_UNLOCK_ITEM_MUTATION } from 'src/app/graphQL/mutations/item-mutations';
import { FDD_GET_LOCKED_ITEMS_BY_USER_QUERY, FDD_GET_PARENT_ITEMS_QUERY } from 'src/app/graphQL/queries/item-queries';
import { GraphQLItemUidAndTypeOutput } from 'src/app/graphQL/models/output/graphql-item-uid-and-type-output';
import { ItemUidAndTypeDto } from 'src/app/models/dto/itemUidAndTypeDto';
import { MigrationConfigService } from '../migration/migration-config.service';
import { ItemType } from 'src/app/models/enums/itemType';
import { FenixDomainDataConfig } from 'src/environments/environment';

const ITEM_TYPE_TO_GO: Partial<Record<ItemType, string>> = {
  [ItemType.TestCase]: 'TEST_CASE',
  [ItemType.TestSuite]: 'TEST_SUITE',
  [ItemType.TestAnalysis]: 'TEST_ANALYSIS',
  [ItemType.TestSpecification]: 'TEST_SPECIFICATION',
  // Lockable but not a test item: eesecoverage-lambda enforces the lock on update/delete.
  // Absent from getParentItems, which only walks the test-item hierarchy.
  [ItemType.EeseCoverage]: 'EESE_COVERAGE',
  // Same contract: softwareversioncoverage-lambda enforces the lock on update/delete.
  [ItemType.SoftwareVersionCoverage]: 'SOFTWARE_VERSION_COVERAGE',
  // Same contract: specificationcoverage-lambda enforces the lock on update/delete.
  [ItemType.SpecificationCoverage]: 'SPECIFICATION_COVERAGE',
};

const GO_TO_ITEM_TYPE: Record<string, ItemType> = {
  'TEST_CASE': ItemType.TestCase,
  'TEST_SUITE': ItemType.TestSuite,
  'TEST_ANALYSIS': ItemType.TestAnalysis,
  'TEST_SPECIFICATION': ItemType.TestSpecification,
  'EESE_COVERAGE': ItemType.EeseCoverage,
  'SOFTWARE_VERSION_COVERAGE': ItemType.SoftwareVersionCoverage,
  'SPECIFICATION_COVERAGE': ItemType.SpecificationCoverage,
};

@Injectable({
  providedIn: 'root',
})
export class ItemsService {

  constructor(
    private apolloClientService: ApolloClientService,
    private migrationConfig: MigrationConfigService
  ) {}

  getParentItems(itemUid: string): Observable<ParentItemBasicInfo[]> {
    // Parent-items is cross-entity and has no dual/compare path — it reads from exactly one
    // source. When items live in Go, resolve parents from Go so the list only contains
    // Go-resident (navigable) items.
    //
    // `displaySource` is only meaningful in `both` mode, so it is consulted there alone:
    // in `old`/`new` the mode already names the single source, and letting displaySource
    // override it would read parents from an API the rest of the page is not using.
    return this.migrationConfig.getEntityConfig$('testSuites').pipe(
      take(1),
      switchMap(config => {
        if (config.mode === 'old') return this.getParentItemsFromOld(itemUid);
        if (config.mode === 'new') return this.getParentItemsFromGo(itemUid);

        return config.displaySource === 'new'
          ? this.getParentItemsFromGo(itemUid)
          : this.getParentItemsFromOld(itemUid);
      })
    );
  }

  private getParentItemsFromOld(itemUid: string): Observable<ParentItemBasicInfo[]> {
    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorParentItems: GraphQLParentItemBasicInfoOutput[]}>({
          query: GET_PARENT_ITEMS_QUERY,
          variables: {ItemUid: itemUid}
        })
      ),
      map(results => {
        const parentItems = results.data.configuratorParentItems
        if (!parentItems)
          return undefined

        return parentItems.map(item => {
          return convertGraphQLParentItemBasicInfoOutputToDto(item);
        })
      })
    )
  }

  private getParentItemsFromGo(itemUid: string): Observable<ParentItemBasicInfo[]> {
    return this.apolloClientService.fenixDomainDataClient$.pipe(
      switchMap(client =>
        client.query<{getParentItems: {uid: string; name: string; itemNo: string; itemType: string}[]}>({
          query: FDD_GET_PARENT_ITEMS_QUERY,
          variables: {itemUid},
          fetchPolicy: 'no-cache'
        })
      ),
      map(results => {
        const parentItems = results.data.getParentItems ?? [];
        return parentItems.map(item => ({
          uid: item.uid,
          name: item.name,
          itemNo: item.itemNo,
          typeName: GO_TO_ITEM_TYPE[item.itemType] ?? ItemType.Unsupported,
        } as ParentItemBasicInfo));
      })
    );
  }

  getCheckedOutItemUidAndType(): Observable<ItemUidAndTypeDto> {
    let userName = localStorage.getItem('userName');

    // Don't make GraphQL query if userName is not available
    if (!userName) {
      console.log('[getCheckedOutItemUidAndType] Skipping query - userName is null');
      return of(undefined);
    }

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorTryGetLockedItemUidAndTypeByUser: GraphQLItemUidAndTypeOutput}>({
          query: TRY_GET_LOCKED_ITEM_UID_AND_TYPE_QUERY,
          variables: {User: userName}
        })
      ),
      map(results => {
        const itemUidAndType = results?.data?.configuratorTryGetLockedItemUidAndTypeByUser;
        if(!itemUidAndType)
          return undefined;

        return convertGraphQLItemUidAndTypeOutputToDto(itemUidAndType);
      })
    );

    return this.migrationConfig.getEntityConfig$('testCases').pipe(
      take(1),
      switchMap(config => {
        if (config.mode === 'old') return oldApiCall$;

        const newApiCall$ = this.apolloClientService.fenixDomainDataClient$.pipe(
          take(1),
          switchMap(client => client.query<any>({
            query: FDD_GET_LOCKED_ITEMS_BY_USER_QUERY,
          })),
          map(result => {
            const locks: any[] = result.data?.getLockedItemsByUser ?? [];
            console.log(`[Migration] getLockedItemsByUser: ${locks.length} lock(s) from Go Lambda`);
            if (locks.length === 0) return undefined;
            // Return the first lock to match old API's single-lock behavior
            const first = locks[0];
            return {
              uid: first.itemUid,
              itemType: GO_TO_ITEM_TYPE[first.itemType] ?? ItemType.Unsupported,
            } as ItemUidAndTypeDto;
          })
        );

        if (config.mode === 'new') {
          console.log('[Migration] getLockedItemsByUser: mode=new, using Go API only');
          return newApiCall$.pipe(
            catchError(err => { console.error('[Migration] getLockedItemsByUser error (new-only mode):', err.message); return of(undefined); })
          );
        }

        return forkJoin({
          old: oldApiCall$.pipe(take(1), catchError(() => of(undefined))),
          new: newApiCall$.pipe(catchError(err => { console.error('[Migration] getLockedItemsByUser error:', err.message); return of(undefined); }))
        }).pipe(
          map(results => {
            console.log(`[Migration] getLockedItemsByUser: old=${results.old?.uid ?? 'none'}, new=${results.new?.uid ?? 'none'}`);
            return config.displaySource === 'new' ? results.new : results.old;
          })
        );
      })
    );
  }

  checkout(itemUid: string, itemType?: ItemType): Observable<boolean> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      take(1),
      switchMap(client =>
        client.mutate<{configuratorTryLockItem: boolean}>({
          mutation: TRY_LOCK_ITEM_MUTATION,
          variables: {ItemUid: itemUid }
        })
      ),
      map(results => results.data.configuratorTryLockItem)
    );

    const goItemType = itemType ? ITEM_TYPE_TO_GO[itemType] : undefined;
    if (!goItemType) return oldApiCall$;

    return this.migrationConfig.getEntityConfig$('testCases').pipe(
      take(1),
      switchMap(config => {
        if (config.mode === 'old') return oldApiCall$;

        const newApiCall$ = this.apolloClientService.fenixDomainDataClient$.pipe(
          take(1),
          switchMap(client => client.mutate<any>({
            mutation: FDD_LOCK_ITEM_MUTATION,
            variables: { itemType: goItemType, itemUid }
          })),
          tap(result => console.log(`[Migration] lockItem Go Lambda result:`, result.data?.lockItem)),
          catchError(err => { console.error(`[Migration] lockItem Go Lambda error:`, err.message); return of(null); })
        );

        if (config.mode === 'new') {
          console.log(`[Migration] lockItem: mode=new, using Go API only (itemType=${goItemType}, uid=${itemUid})`);
          // A failed lock resolves to `false` (not acquired), which is the safe answer here —
          // the caller treats it as "someone else holds it" rather than silently editing.
          return newApiCall$.pipe(map(result => result?.data?.lockItem?.acquired ?? false));
        }

        console.log(`[Migration] lockItem: mode=both, calling both APIs (itemType=${goItemType}, uid=${itemUid})`);
        return forkJoin({
          old: oldApiCall$.pipe(catchError(() => of(false))),
          new: newApiCall$
        }).pipe(map(results => {
          if (config.displaySource === 'new') {
            return results.new?.data?.lockItem?.acquired ?? false;
          }
          return results.old;
        }));
      })
    );
  }

  unlock(itemUid: string, itemType?: ItemType): Observable<void> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      take(1),
      switchMap(client =>
        client.mutate({
          mutation: UNLOCK_ITEM_MUTATION,
          variables: {ItemUid: itemUid }
        })
      ),
      map(() => void 0)
    );

    const goItemType = itemType ? ITEM_TYPE_TO_GO[itemType] : undefined;
    if (!goItemType) return oldApiCall$;

    return this.migrationConfig.getEntityConfig$('testCases').pipe(
      take(1),
      switchMap(config => {
        if (config.mode === 'old') return oldApiCall$;

        const newApiCall$ = this.apolloClientService.fenixDomainDataClient$.pipe(
          take(1),
          switchMap(client => client.mutate<any>({
            mutation: FDD_UNLOCK_ITEM_MUTATION,
            variables: { itemType: goItemType, itemUid }
          })),
          tap(result => console.log(`[Migration] unlockItem Go Lambda result:`, result.data?.unlockItem)),
          catchError(err => { console.error(`[Migration] unlockItem Go Lambda error:`, err.message); return of(null); })
        );

        if (config.mode === 'new') {
          console.log(`[Migration] unlockItem: mode=new, using Go API only (itemType=${goItemType}, uid=${itemUid})`);
          return newApiCall$.pipe(map(() => void 0));
        }

        console.log(`[Migration] unlockItem: mode=both, calling both APIs (itemType=${goItemType}, uid=${itemUid})`);
        return forkJoin({
          old: oldApiCall$.pipe(catchError(() => of(void 0))),
          new: newApiCall$
        }).pipe(map(() => void 0));
      })
    );
  }
}
